import { useMemo } from "react";
import { MeRole } from "#/apis/infra/getMe";
import { useOrg } from "./OrgContext";

// Effective role names from the internal API (1.5.3). The internal scope_type for each is
// SYSTEM / CUSTOMER / LICENSE — UI terms Territory(=CUSTOMER) / Organization(=LICENSE).
export const ROLE = {
  SYSTEM_ADMIN: "SYSTEM_ADMIN",
  TERRITORY_ADMIN: "TERRITORY_ADMIN",
  ORG_MANAGER: "ORG_MANAGER",
  ORG_USER: "ORG_USER",
  ORG_AUDITOR: "ORG_AUDITOR",
} as const;

export type Rbac = {
  roles: MeRole[];
  userId: string | null;
  // False until GET /me has resolved — gate redirects on this to avoid bouncing a manager
  // before roles load (Cognito loads them asynchronously).
  rolesLoaded: boolean;
  isSystemAdmin: boolean;
  isTerritoryAdmin: boolean;
  isOrgManager: boolean;
  // Can reach the user-management page at all (entry point + route guard).
  canManageUsers: boolean;
  canCreateUser: boolean;
  // V1: disable-user is documented SYSTEM_ADMIN-only (see rbac-risks-explained #4).
  canDisableUser: boolean;
  // Update License (= edit Organization) is SYSTEM scope only.
  canEditOrg: boolean;
  canSwitchTerritory: boolean;
  // Whether the given user id is the caller (for self-action guards).
  isSelf: (id: string) => boolean;
};

// Derives UI capability flags from the caller's effective roles (GET /me, retained by
// OrgContext). Single source of RBAC gating — components read a flag, never a role string.
// NOTE: in Cognito mode OrgContext does not call /me, so roles is empty and every management
// flag is false. A Cognito-targeted RBAC page would need an explicit getMe() here first.
export function useRbac(): Rbac {
  const { roles, userId, meLoaded } = useOrg();

  return useMemo<Rbac>(() => {
    const has = (role: string): boolean => roles.some((r) => r.role === role);
    const isSystemAdmin = has(ROLE.SYSTEM_ADMIN);
    const isTerritoryAdmin = has(ROLE.TERRITORY_ADMIN);
    const isOrgManager = has(ROLE.ORG_MANAGER);
    const canManageUsers = isSystemAdmin || isTerritoryAdmin || isOrgManager;

    return {
      roles,
      userId,
      rolesLoaded: meLoaded,
      isSystemAdmin,
      isTerritoryAdmin,
      isOrgManager,
      canManageUsers,
      canCreateUser: canManageUsers,
      canDisableUser: isSystemAdmin,
      canEditOrg: isSystemAdmin,
      canSwitchTerritory: isSystemAdmin || isTerritoryAdmin,
      isSelf: (id: string): boolean => !!userId && id === userId,
    };
  }, [roles, userId, meLoaded]);
}

export default useRbac;
